import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Heart, Sparkles, Flower2 } from "lucide-react";
import ImmersiveLayout from "../components/ImmersiveLayout";
import { useLanguage } from "../contexts/LanguageContext";

/**
 * Immersive Memory Bouquet Intro — dark cinematic
 * Three pillars + CTA to begin the conversational flow.
 */
export default function MemoryBouquetIntro() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const id = language === "id";

  const pillars = [
    {
      icon: Heart,
      title: id ? "Ceritakan Perasaan" : "Share the Feeling",
      desc: id ? "Tuangkan cerita, kenangan, atau emosi yang ingin disampaikan." : "Pour out the story, memory, or emotion you want to convey."
    },
    {
      icon: Sparkles,
      title: id ? "AI Merangkai" : "AI Weaves",
      desc: id ? "Curator AI menerjemahkan ceritamu menjadi pilihan bunga ideal." : "Our AI Curator translates your story into the ideal bloom selection."
    },
    {
      icon: Flower2,
      title: id ? "Florist Menyusun" : "Florists Craft",
      desc: id ? "Florist berpengalaman merangkai setiap tangkai dengan tangan." : "Experienced florists arrange every stem by hand."
    }
  ];

  return (
    <ImmersiveLayout>
      {/* HERO */}
      <section className="relative min-h-[100vh] pt-32 pb-20 px-6 lg:px-16 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1594038658121-219eda06322c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-seikat-ink/65" />
          <div className="absolute inset-0 bg-gradient-to-t from-seikat-ink via-seikat-ink/40 to-transparent" />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="space-y-8 text-center"
          >
            <div className="flex items-center justify-center gap-3">
              <span className="w-8 h-px bg-seikat-pink" />
              <span className="eyebrow-immersive">Memory Bouquet</span>
              <span className="w-8 h-px bg-seikat-pink" />
            </div>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl text-seikat-bone leading-[0.95] tracking-tight">
              {id ? <>Setiap bunga membawa <em className="text-seikat-pink">sebuah cerita</em>.</> : <>Every flower carries <em className="text-seikat-pink">a story</em>.</>}
            </h1>
            <p className="font-body text-seikat-bone/75 text-base lg:text-lg max-w-2xl mx-auto font-light leading-relaxed">
              {id
                ? "Memory Bouquet adalah percakapan singkat dengan AI Curator kami yang menerjemahkan momen, perasaan, dan kepribadian penerima menjadi rangkaian bunga yang sungguh berbicara."
                : "Memory Bouquet is a brief conversation with our AI Curator that translates the moment, feeling, and personality of the recipient into a truly speaking arrangement."}
            </p>
          </motion.div>

          {/* Three Pillars */}
          <div className="mt-20 grid sm:grid-cols-3 gap-6">
            {pillars.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.7, delay: 0.15 + i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="glass-dark rounded-2xl p-7 text-center group hover:bg-white/8 transition-colors"
                data-testid={`pillar-${i}`}
              >
                <div className="w-12 h-12 rounded-full bg-seikat-pink/15 border border-seikat-pink/30 flex items-center justify-center mx-auto mb-5">
                  <p.icon size={18} className="text-seikat-pink" strokeWidth={1.4} />
                </div>
                <h3 className="font-heading text-2xl text-seikat-bone mb-2">{p.title}</h3>
                <p className="font-body text-sm text-seikat-bone/65 leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="flex justify-center mt-16"
          >
            <button
              onClick={() => navigate("/memory-bouquet/create")}
              className="btn-pill btn-pill--solid group"
              data-testid="memory-intro-begin-btn"
              data-magnet
            >
              {id ? "Mulai Percakapan" : "Begin the Conversation"}
              <ArrowRight size={14} className="ml-1 group-hover:translate-x-1 transition-transform" />
            </button>
          </motion.div>
        </div>
      </section>
    </ImmersiveLayout>
  );
}
